// Shared cart helper used by B2B cards, collection bulk ordering, compatible parts and upsells.
// It follows Impact's bundled-section and cart-event contracts without modifying theme.js.
async function addItems({ items, openDrawer = true } = {}) {
  if (!Array.isArray(items) || items.length === 0) throw new Error('addItems: items are required');

  const routeRoot = window.Shopify?.routes?.root || '/';
  const sections = window.themeVariables?.settings?.cartType === 'popover' ? ['variant-added'] : [];
  document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
    bubbles: true,
    detail: { sections },
  }));

  const response = await fetch(`${routeRoot}cart/add.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ items, sections, sections_url: `${window.location.pathname}${window.location.search}` }),
  });
  const responseText = await response.text();
  let responseJson;

  try {
    responseJson = JSON.parse(responseText);
  } catch {
    responseJson = { message: responseText.slice(0, 200) };
  }

  if (!response.ok) {
    const errorMessage = responseJson.description || responseJson.message || 'Cart add failed';
    document.documentElement.dispatchEvent(new CustomEvent('cart:error', {
      bubbles: true,
      detail: { error: errorMessage },
    }));
    throw new Error(errorMessage);
  }

  const cart = await (await fetch(`${routeRoot}cart.js`)).json();
  cart.sections = responseJson.sections || {};

  document.documentElement.dispatchEvent(new CustomEvent('variant:add', {
    bubbles: true,
    detail: {
      items: responseJson.items || [responseJson],
      cart,
      blockCartDrawerOpening: !openDrawer,
    },
  }));
  document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
    bubbles: true,
    detail: { baseEvent: 'variant:add', cart },
  }));

  return cart;
}

function addToCart({ variantId, quantity = 1, deliveryTime = '' } = {}) {
  if (!variantId) throw new Error('addToCart: variantId is required');

  const item = { id: variantId, quantity };
  if (deliveryTime) item.properties = { _delivery_time: deliveryTime };
  return addItems({ items: [item] });
}

async function changeLineItem({ lineKey, quantity, lineItem } = {}) {
  if (!lineKey) throw new Error('changeLineItem: line key is required');

  const targetQuantity = Math.max(0, Number.parseInt(quantity, 10) || 0);
  const routeRoot = window.Shopify?.routes?.root || '/';
  const sections = [];

  document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
    bubbles: true,
    detail: { sections },
  }));
  lineItem?.dispatchEvent(new CustomEvent('line-item:will-change', {
    bubbles: true,
    detail: { targetQuantity },
  }));

  const response = await fetch(`${routeRoot}cart/change.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      id: lineKey,
      quantity: targetQuantity,
      sections,
    }),
  });
  const responseText = await response.text();
  let responseJson;

  try {
    responseJson = JSON.parse(responseText);
  } catch {
    responseJson = { message: responseText.slice(0, 200) };
  }

  if (!response.ok) {
    const errorMessage = responseJson.description || responseJson.message || 'Cart update failed';
    document.documentElement.dispatchEvent(new CustomEvent('cart:error', {
      bubbles: true,
      detail: { error: errorMessage },
    }));
    throw new Error(errorMessage);
  }

  const updatedLine = responseJson.items?.find((item) => item.key === lineKey);
  lineItem?.dispatchEvent(new CustomEvent('line-item:change', {
    bubbles: true,
    detail: {
      quantity: updatedLine?.quantity || 0,
      cart: responseJson,
    },
  }));
  document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
    bubbles: true,
    detail: { baseEvent: 'line-item:change', cart: responseJson },
  }));

  return responseJson;
}

// Quantity math mirrors the input.min/step/max contract Impact's quantity-selector relies on, so
// +/- clicks and typed edits land on exactly the values the input itself would allow.
function readQuantityBounds(input) {
  return {
    min: Number.parseFloat(input.min) || 1,
    step: Number.parseFloat(input.step) || 1,
    max: input.max ? Number.parseFloat(input.max) : null,
  };
}

function clampQuantity(input, value) {
  const { min, step, max } = readQuantityBounds(input);
  let target = Number.parseInt(value, 10);
  if (!Number.isFinite(target)) return min;
  target = min + Math.floor((target - min) / step) * step;
  if (max !== null) target = Math.min(target, max);
  return Math.max(min, target);
}

// Freezes the whole line while a change is in flight. The capture listeners below swallow every
// further click inside the line until the request settles, so double clicks and rapid +/- taps
// cannot stack parallel cart/change.js requests.
function requestLineChange({ lineItem, lineKey, quantity }) {
  if (!lineItem || !lineKey || lineItem.hasAttribute('data-updating')) return;

  lineItem.setAttribute('data-updating', '');
  lineItem.setAttribute('aria-busy', 'true');
  changeLineItem({ lineKey, quantity, lineItem })
    .catch((error) => console.error('fat-cart: line update failed', error))
    .finally(() => {
      lineItem.removeAttribute('data-updating');
      lineItem.removeAttribute('aria-busy');
      // changeLineItem only clears the pill loader via line-item:change on success; clear it here
      // too so a failed request does not leave the spinner running.
      lineItem.querySelector('pill-loader')?.removeAttribute('aria-busy');
    });
}

// Capture drawer line actions before Impact's generic controls (quantity-selector, line-item-
// quantity) can fire their own updates, and swallow every click inside a frozen line.
document.addEventListener('click', (event) => {
  const frozenLine = event.target.closest?.('line-item[data-updating]');
  if (frozenLine) {
    event.preventDefault();
    event.stopImmediatePropagation();
    return;
  }

  const control = event.target.closest?.('[data-fat-cart-remove], [data-fat-cart-decrease], [data-fat-cart-increase]');
  if (!control) return;

  const actions = control.closest('[data-fat-cart-actions]');
  if (!actions) return;

  event.preventDefault();
  event.stopImmediatePropagation();

  const input = actions.querySelector('input[data-line-key]');
  const lineItem = actions.closest('line-item');
  const lineKey = actions.dataset.lineKey;

  if (control.hasAttribute('data-fat-cart-remove')) {
    requestLineChange({ lineItem, lineKey, quantity: 0 });
    return;
  }

  const currentQuantity = Number.parseInt(input?.value, 10) || 0;
  const { min, step, max } = readQuantityBounds(input);
  let nextQuantity;

  if (control.hasAttribute('data-fat-cart-decrease')) {
    nextQuantity = currentQuantity - step;
    // Stepping below the quantity rule minimum removes the line entirely.
    if (nextQuantity < min) {
      requestLineChange({ lineItem, lineKey, quantity: 0 });
      return;
    }
  } else {
    if (max !== null && currentQuantity >= max) return;
    nextQuantity = Math.min(currentQuantity + step, max ?? Number.MAX_SAFE_INTEGER);
  }

  input.value = String(nextQuantity);
  input.dataset.quantity = String(nextQuantity);
  requestLineChange({ lineItem, lineKey, quantity: nextQuantity });
}, true);

// Typed edits and any other change events inside the drawer: clamp to the quantity rules, revert
// while the line is frozen, and route through the same guarded path. stopImmediatePropagation
// keeps Impact's line-item-quantity and quantity-input handlers out so nothing double-fires.
document.addEventListener('change', (event) => {
  const input = event.target;
  if (!(input instanceof HTMLInputElement) || !input.hasAttribute('data-line-key')) return;

  const actions = input.closest('[data-fat-cart-actions]');
  if (!actions) return;

  event.stopImmediatePropagation();

  const lineItem = actions.closest('line-item');
  if (lineItem?.hasAttribute('data-updating')) {
    input.value = input.dataset.quantity || input.value;
    return;
  }

  const committedQuantity = Number.parseInt(input.dataset.quantity, 10);
  const nextQuantity = clampQuantity(input, input.value);
  input.value = String(nextQuantity);
  input.dataset.quantity = String(nextQuantity);

  if (Number.isFinite(committedQuantity) && nextQuantity === committedQuantity) return;

  requestLineChange({ lineItem, lineKey: actions.dataset.lineKey, quantity: nextQuantity });
}, true);

window.FatCart = Object.assign(window.FatCart || {}, { addItems, addToCart, changeLineItem });
